// src/pages/Dashboard.jsx - FIXED: Single Event Fetch + Rate-Limited Chat
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Card from '../components/common/Card';
import Badge from '../components/common/Badge';
import Loading from '../components/common/Loading';
import { assetsService } from '../services/assets';
import { eventsService } from '../services/events';
import { policiesService } from '../services/policies';
import { edgeNodesService } from '../services/edge-nodes';
import { sendChatQuery } from '../services/chat';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const Dashboard = () => {
  const [stats, setStats] = useState({
    assets: { count: 0, loading: true },
    events: { count: 0, urgent: 0, loading: true },
    policies: { count: 0, loading: true },
    edgeNodes: { count: 0, online: 0, loading: true }
  });

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [lastChatRequestTime, setLastChatRequestTime] = useState(0); // ← ADDED: Rate limiting
  const messagesEndRef = useRef(null);

  // Recent events state
  const [recentEvents, setRecentEvents] = useState([]);
  const [recentEventsLoading, setRecentEventsLoading] = useState(true);

  // Constants
  const MIN_CHAT_INTERVAL = 10000; // 10 seconds between chat requests

  // ═══════════════════════════════════════════════════════════
  // FIX #1: SINGLE DATA FETCH - Combines all API calls
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        console.log('📊 Dashboard: Fetching all data...');
        
        // Fetch all data in parallel with a single events call
        const [assets, allEvents, policies, edgeNodes] = await Promise.all([
          assetsService.getAssets(),
          eventsService.getEvents({ limit: 50 }), // ← SINGLE EVENT FETCH (was 2 before)
          policiesService.getPolicies(),
          edgeNodesService.getEdgeNodes()
        ]);

        console.log('✅ Dashboard: Data fetched successfully', {
          assets: assets.length,
          events: allEvents.length,
          policies: policies.length,
          edgeNodes: edgeNodes.length
        });

        // Calculate urgent events from the single fetch
        const urgentEvents = allEvents.filter(
          event => ['High', 'Critical'].includes(event.severity) && 
                   event.status !== 'Resolved'
        );

        // Update all stats at once
        setStats({
          assets: { count: assets.length, loading: false },
          events: { 
            count: allEvents.length, 
            urgent: urgentEvents.length, 
            loading: false 
          },
          policies: { count: policies.length, loading: false },
          edgeNodes: { 
            count: edgeNodes.length, 
            online: edgeNodes.filter(n => n.status === 'Online').length,
            loading: false 
          }
        });

        // Set recent events (first 5 from the same fetch)
        setRecentEvents(allEvents.slice(0, 5));
        setRecentEventsLoading(false);

      } catch (error) {
        console.error('❌ Dashboard: Error fetching data:', error);
        
        // Set loading to false for all on error
        setStats(prev => ({
          assets: { ...prev.assets, loading: false },
          events: { ...prev.events, loading: false },
          policies: { ...prev.policies, loading: false },
          edgeNodes: { ...prev.edgeNodes, loading: false }
        }));
        setRecentEventsLoading(false);
      }
    };

    fetchDashboardData();
  }, []); // Run once on mount

  // ═══════════════════════════════════════════════════════════
  // FIX #2: RATE-LIMITED CHAT - No artificial delays
  // ═══════════════════════════════════════════════════════════
  const handleChatSend = async () => {
    if (!chatInput.trim()) return;
    
    // Rate limiting check
    const now = Date.now();
    const timeSinceLastRequest = now - lastChatRequestTime;
    
    if (timeSinceLastRequest < MIN_CHAT_INTERVAL) {
      const waitTime = Math.ceil((MIN_CHAT_INTERVAL - timeSinceLastRequest) / 1000);
      alert(`⏱️ Please wait ${waitTime} seconds before sending another message.`);
      return;
    }
    
    setLastChatRequestTime(now);
    
    const userMessage = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMessage]);
    
    const currentInput = chatInput;
    setChatInput('');
    setChatLoading(true);

    try {
      // REMOVED: setTimeout artificial delay
      // Send only recent history (last 6 messages) to reduce payload
      const recentHistory = chatMessages.slice(-6);
      const response = await sendChatQuery(
        currentInput, 
        [...recentHistory, userMessage]
      );
      
      const assistantMessage = { 
        role: response.role, 
        content: response.content 
      };
      setChatMessages(prev => [...prev, assistantMessage]);
      
    } catch (error) {
      console.error('❌ Dashboard Chat: Failed to fetch AI response:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.' 
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleChatKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChatSend();
    }
  };

  // ═══════════════════════════════════════════════════════════
  // FIX #3: RATE-LIMITED QUICK QUESTIONS
  // ═══════════════════════════════════════════════════════════
  const handleQuickQuestion = (question) => {
    const now = Date.now();
    const timeSinceLastRequest = now - lastChatRequestTime;
    
    if (timeSinceLastRequest < MIN_CHAT_INTERVAL) {
      const waitTime = Math.ceil((MIN_CHAT_INTERVAL - timeSinceLastRequest) / 1000);
      alert(`⏱️ Please wait ${waitTime} seconds before asking another question.`);
      return;
    }
    
    setChatInput(question);
    setTimeout(() => handleChatSend(), 100);
  };

  // Helper to check if chat can be sent
  const canSendChat = () => {
    const timeSinceLastRequest = Date.now() - lastChatRequestTime;
    return !chatLoading && 
           chatInput.trim() && 
           timeSinceLastRequest >= MIN_CHAT_INTERVAL;
  };

  // Helper to get time until next request allowed
  const getTimeUntilNextRequest = () => {
    const timeSince = Date.now() - lastChatRequestTime;
    return Math.max(0, MIN_CHAT_INTERVAL - timeSince);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  return (
    <div className="space-y-6">
      {/* Metrics Grid - Larger fonts like Nimbus */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {/* OT Assets Card */}
        <div className="relative bg-white overflow-hidden rounded-lg border border-gray-200 shadow-sm">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-blue-400"></div>
          <div className="p-5">
            <div className="flex items-center mb-3">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-10 w-10 rounded-md bg-blue-100 text-blue-700">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">OT Assets</p>
              </div>
            </div>
            
            {stats.assets.loading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                <p className="text-3xl font-bold text-gray-900 mb-1">{stats.assets.count}</p>
                <p className="text-xs text-gray-500">Total monitored assets</p>
                <div className="mt-3">
                  <Link 
                    to="/assets" 
                    className="text-xs font-medium text-blue-700 hover:text-blue-600 flex items-center group"
                  >
                    View all assets
                    <svg className="ml-1 h-3 w-3 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Security Events Card */}
        <div className="relative bg-white overflow-hidden rounded-lg border border-gray-200 shadow-sm">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-orange-400"></div>
          <div className="p-5">
            <div className="flex items-center mb-3">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-10 w-10 rounded-md bg-red-100 text-red-700">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Security Events</p>
              </div>
            </div>
            
            {stats.events.loading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
              </div>
            ) : (
              <>
                <div className="flex items-baseline">
                  <p className="text-3xl font-bold text-gray-900">{stats.events.count}</p>
                  {stats.events.urgent > 0 && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                      {stats.events.urgent} urgent
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">Total security events</p>
                <div className="mt-3">
                  <Link 
                    to="/events" 
                    className="text-xs font-medium text-red-700 hover:text-red-600 flex items-center group"
                  >
                    View all events
                    <svg className="ml-1 h-3 w-3 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Security Policies Card */}
        <div className="relative bg-white overflow-hidden rounded-lg border border-gray-200 shadow-sm">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 to-emerald-400"></div>
          <div className="p-5">
            <div className="flex items-center mb-3">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-10 w-10 rounded-md bg-green-100 text-green-700">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Security Policies</p>
              </div>
            </div>
            
            {stats.policies.loading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
              </div>
            ) : (
              <>
                <p className="text-3xl font-bold text-gray-900 mb-1">{stats.policies.count}</p>
                <p className="text-xs text-gray-500">Active policies</p>
                <div className="mt-3">
                  <Link 
                    to="/policies" 
                    className="text-xs font-medium text-green-700 hover:text-green-600 flex items-center group"
                  >
                    Manage policies
                    <svg className="ml-1 h-3 w-3 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Edge Nodes Card */}
        <div className="relative bg-white overflow-hidden rounded-lg border border-gray-200 shadow-sm">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-indigo-400"></div>
          <div className="p-5">
            <div className="flex items-center mb-3">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-10 w-10 rounded-md bg-purple-100 text-purple-700">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                  </svg>
                </div>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Edge Nodes</p>
              </div>
            </div>
            
            {stats.edgeNodes.loading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
              </div>
            ) : (
              <>
                <div className="flex items-baseline">
                  <p className="text-3xl font-bold text-gray-900">{stats.edgeNodes.count}</p>
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    {stats.edgeNodes.online} online
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Total edge nodes</p>
                <div className="mt-3">
                  <Link 
                    to="/edge-nodes" 
                    className="text-xs font-medium text-purple-700 hover:text-purple-600 flex items-center group"
                  >
                    View all nodes
                    <svg className="ml-1 h-3 w-3 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area - Horizontal Layout */}
      <div className="grid grid-cols-3 gap-4">
        {/* Left/Center - AI Sentry Chat (Takes 2 columns) */}
        <div className="col-span-2">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm h-[600px] flex flex-col">
            <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-lg flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <span className="text-blue-700 text-lg">💡</span>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">AI Sentry</h2>
                  <p className="text-xs text-gray-600">Ask about your OT security posture</p>
                </div>
              </div>
              <Link
                to="/chat"
                className="text-xs font-medium text-blue-700 hover:text-blue-600 flex items-center"
              >
                Full Chat
                <svg className="ml-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 bg-gray-50">
              {chatMessages.length === 0 ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-700 mb-4">
                    <span className="text-2xl">🤖</span>
                  </div>
                  <h3 className="text-md font-semibold text-gray-900 mb-2">Ready to assist</h3>
                  <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">
                    I can help with security analysis, threat hunting, and operational intelligence.
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    <button
                      onClick={() => handleQuickQuestion("What critical security alerts do we have?")}
                      disabled={!canSendChat()}
                      className="px-3 py-2 text-sm font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Security Alerts
                    </button>
                    <button
                      onClick={() => handleQuickQuestion("Show me recent asset discoveries")}
                      disabled={!canSendChat()}
                      className="px-3 py-2 text-sm font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Asset Discoveries
                    </button>
                    <button
                      onClick={() => handleQuickQuestion("Analyze current policy compliance")}
                      disabled={!canSendChat()}
                      className="px-3 py-2 text-sm font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Policy Compliance
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {chatMessages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      {msg.role === 'user' ? (
                        <div className="flex items-end max-w-[80%]">
                          <div className="bg-blue-700 text-white px-4 py-3 rounded-2xl rounded-br-md shadow-sm mr-3">
                            <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                          </div>
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-blue-700 text-sm">👤</span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start max-w-[80%]">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center mr-3">
                            <span className="text-gray-600 text-sm">💡</span>
                          </div>
                          <div className="bg-white border border-gray-200 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm">
                            <div className="text-sm text-gray-800">
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  p: ({node, ...props}) => <p className="mb-2 last:mb-0" {...props} />,
                                  strong: ({node, ...props}) => <strong className="font-semibold text-gray-900" {...props} />,
                                  ul: ({node, ...props}) => <ul className="list-disc pl-4 mb-2" {...props} />,
                                  li: ({node, ...props}) => <li className="mb-1" {...props} />,
                                  table: ({node, ...props}) => (
                                    <div className="overflow-x-auto my-3">
                                      <table className="min-w-full divide-y divide-gray-300 border border-gray-300" {...props} />
                                    </div>
                                  ),
                                  thead: ({node, ...props}) => <thead className="bg-gray-50" {...props} />,
                                  tbody: ({node, ...props}) => <tbody className="divide-y divide-gray-200 bg-white" {...props} />,
                                  tr: ({node, ...props}) => <tr {...props} />,
                                  th: ({node, ...props}) => (
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900 border-r border-gray-300 last:border-r-0" {...props} />
                                  ),
                                  td: ({node, ...props}) => (
                                    <td className="px-3 py-2 text-xs text-gray-700 border-r border-gray-200 last:border-r-0" {...props} />
                                  ),
                                }}
                              >
                                {msg.content}
                              </ReactMarkdown>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {chatLoading && (
                <div className="flex items-center justify-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center mr-3">
                    <span className="text-gray-600 text-sm">💡</span>
                  </div>
                  <div className="bg-white border border-gray-200 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm flex items-center space-x-3">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                    <span className="text-sm text-gray-700">AI Sentry is thinking...</span>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Chat Input */}
            <div className="p-4 border-t border-gray-200 bg-white rounded-b-lg">
              <div className="flex space-x-3">
                <div className="flex-1">
                  <textarea
                    rows="2"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={handleChatKeyPress}
                    placeholder="Ask AI Sentry about your OT environment..."
                    className="w-full resize-none border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    disabled={chatLoading}
                  />
                  {lastChatRequestTime > 0 && getTimeUntilNextRequest() > 0 && (
                    <div className="mt-1 text-xs text-orange-600">
                      ⏱️ Next request in {Math.ceil(getTimeUntilNextRequest() / 1000)}s
                    </div>
                  )}
                </div>
                <button
                  onClick={handleChatSend}
                  disabled={!canSendChat()}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    !canSendChat()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                      : 'bg-blue-700 text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                  }`}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Sidebar - Recent Events & Quick Actions */}
        <div className="space-y-4">
          {/* Recent Security Events */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 rounded-t-lg">
              <h2 className="text-sm font-semibold text-gray-900">Recent Security Events</h2>
            </div>
            
            <div className="p-3">
              {recentEventsLoading ? (
                <Loading text="Loading recent events..." />
              ) : recentEvents.length === 0 ? (
                <p className="text-center text-gray-500 py-2 text-xs">No recent events found</p>
              ) : (
                <div className="space-y-2">
                  {recentEvents.map((event, index) => (
                    <div key={index} className="flex items-center py-2 border-b border-gray-100 last:border-b-0">
                      <div className={`flex-shrink-0 w-1.5 h-1.5 rounded-full ${
                        event.severity === 'Critical' ? 'bg-red-500' :
                        event.severity === 'High' ? 'bg-yellow-500' : 'bg-blue-500'
                      }`}></div>
                      
                      <div className="ml-2 flex-1">
                        <div className="flex items-center mb-1">
                          <Badge type={event.severity} />
                          <span className="ml-2 text-xs font-medium text-gray-900">
                            {event.event_type}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">{event.description}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(event.timestamp).toLocaleString()}
                        </p>
                      </div>
                      
                      <div className="ml-2">
                        <Link
                          to={`/events/${event.event_id}`}
                          className="text-xs font-medium text-green-700 hover:text-green-600 transition-colors"
                        >
                          Details
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 rounded-t-lg">
              <h2 className="text-sm font-semibold text-gray-900">Quick Actions</h2>
            </div>
            
            <div className="p-3 space-y-2">
              <button
                onClick={() => handleQuickQuestion("Investigate critical security alerts")}
                disabled={!canSendChat()}
                className="w-full flex items-center px-3 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-xs mr-2">🔍</span>
                <span className="font-medium text-xs">Investigate Critical Alert</span>
              </button>
              
              <Link
                to="/policies/new"
                className="w-full flex items-center px-3 py-2 bg-green-50 text-green-800 rounded-lg hover:bg-green-100 transition-colors group"
              >
                <span className="text-xs mr-2">📋</span>
                <span className="font-medium text-xs">Create Security Policy</span>
              </Link>
              
              <button
                onClick={() => handleQuickQuestion("Analyze current system performance and security posture")}
                disabled={!canSendChat()}
                className="w-full flex items-center px-3 py-2 bg-purple-50 text-purple-800 rounded-lg hover:bg-purple-100 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-xs mr-2">🤖</span>
                <span className="font-medium text-xs">AI System Analysis</span>
              </button>
              
              <button
                onClick={() => handleQuickQuestion("Show me emergency lockdown procedures")}
                disabled={!canSendChat()}
                className="w-full flex items-center px-3 py-2 bg-yellow-50 text-yellow-800 rounded-lg hover:bg-yellow-100 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-xs mr-2">🔒</span>
                <span className="font-medium text-xs">Emergency Lockdown</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
